import { PromotionStatus } from 'src/models/promotion/PromotionTypes';
import { StatusIndicatorProps } from '@amzn/awsui-components-react';

// Column definitions for the Promotion History table
export const PROMOTION_HISTORY_COLUMN_DEFINITIONS = {
    TIMESTAMP: {
        id: 'promotedAtDisplay',
        header: 'Timestamp',
        sortingField: 'promotedAt',
    },
    STATUS: {
        id: 'statusDisplay',
        header: 'Status',
        sortingField: 'statusDisplay',
    },
    COMPONENT_TYPE: {
        id: 'componentType',
        header: 'Component Type',
        sortingField: 'componentType',
    },
    COMPONENT_NAME: {
        id: 'componentNameDisplay',
        header: 'Component Name',
        sortingField: 'componentNameDisplay',
    },
    SOURCE: {
        id: 'source',
        header: 'Source',
    },
    TARGET: {
        id: 'target',
        header: 'Target',
    },
    PERIOD: {
        id: 'accountingPeriod',
        header: 'Period',
        sortingField: 'accountingPeriod',
    },
    PROMOTED_BY: {
        id: 'promotedByDisplay',
        header: 'Promoted By',
        sortingField: 'promotedByDisplay',
    },
} as const;

export const PROMOTION_HISTORY_COLUMN_IDS =
    Object.values(PROMOTION_HISTORY_COLUMN_DEFINITIONS).map(col => col.id);

export const PROMOTION_HISTORY_DEFAULT_PREFERENCES = {
    pageSize: 20,
    visibleColumns: PROMOTION_HISTORY_COLUMN_IDS,
    wrapLines: false,
    stripedRows: false,
    contentDisplay: PROMOTION_HISTORY_COLUMN_IDS.map(id => ({ id, visible: true })),
    stickyColumns: { first: 0, last: 0 },
};

export const PROMOTION_HISTORY_PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10 promotions' },
    { value: 20, label: '20 promotions' },
    { value: 50, label: '50 promotions' },
    { value: 100, label: '100 promotions' },
];

export const PROMOTION_HISTORY_VISIBLE_CONTENT_OPTIONS =
    Object.values(PROMOTION_HISTORY_COLUMN_DEFINITIONS).map(col => ({
        id: col.id,
        label: col.header,
        editable: false,
    }));

// "Find resources" property filter — searchable columns
export const PROMOTION_HISTORY_FILTERING_PROPERTIES = [
    PROMOTION_HISTORY_COLUMN_DEFINITIONS.STATUS,
    PROMOTION_HISTORY_COLUMN_DEFINITIONS.COMPONENT_TYPE,
    PROMOTION_HISTORY_COLUMN_DEFINITIONS.COMPONENT_NAME,
    PROMOTION_HISTORY_COLUMN_DEFINITIONS.PROMOTED_BY,
    PROMOTION_HISTORY_COLUMN_DEFINITIONS.TIMESTAMP,
].map(column => ({
    key: column.id,
    operators: [':', '!:', '=', '!='],
    propertyLabel: column.header,
    groupValuesLabel: `${column.header} values`,
}));

// StatusIndicator rendering per promotion status: [display label, indicator type]
export const PROMOTION_STATUS_INDICATOR_CONFIG: Record<string, [string, StatusIndicatorProps.Type]> = {
    [PromotionStatus.COMPLETED]: ['Completed', 'success'],
    [PromotionStatus.FAILED]: ['Failed', 'error'],
    [PromotionStatus.VALIDATING]: ['In progress', 'in-progress'],
    [PromotionStatus.PROMOTING]: ['In progress', 'in-progress'],
    [PromotionStatus.BLOCKED]: ['Blocked', 'warning'],
    [PromotionStatus.PENDING_SUBMISSION_REQUIRES_ACTION]: ['Completed', 'warning'],
};

// Number of months (including the current open period) shown in the period dropdown
export const PROMOTION_HISTORY_PERIOD_MONTHS = 12;

// Query-string key used to make the selected accounting period shareable via URL.
// Values are the API period format (e.g. "JUL-26").
export const PROMOTION_HISTORY_PERIOD_URL_PARAM = 'period';

// Query-string key that makes the selected tab shareable, alongside the period key.
export const PROMOTION_HISTORY_TAB_URL_PARAM = 'tab';

// The two tabs the page switches between. Not bulk-specific: the view owns both.
export const PROMOTION_HISTORY_TAB_IDS = {
    PROMOTIONS: 'promotions',
    BULK_PROMOTIONS: 'bulk-promotions',
} as const;

